import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, Clock, Zap, CalendarClock, ArrowRight, 
  Timer, AlertCircle, CheckCircle2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { PROJECT_DURATION_LABELS, URGENCY_LEVEL_LABELS, ProjectDuration, UrgencyLevel } from '@/types/jobWizard';
import { format, parseISO, differenceInDays, isPast, isFuture, isToday } from 'date-fns';
import { motion } from 'framer-motion';

interface JobContractTimelineCardProps {
  contractStartDate?: string;
  contractEndDate?: string;
  projectDuration?: ProjectDuration;
  urgencyLevel?: UrgencyLevel;
}

const URGENCY_CONFIG: Record<UrgencyLevel, { color: string; bgColor: string; icon: React.ElementType }> = {
  asap: { 
    color: 'text-red-700 dark:text-red-400', 
    bgColor: 'bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-700/40',
    icon: Zap 
  },
  within_1_week: { 
    color: 'text-orange-700 dark:text-orange-400', 
    bgColor: 'bg-orange-100 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700/40',
    icon: AlertCircle 
  },
  within_1_month: { 
    color: 'text-amber-700 dark:text-amber-400', 
    bgColor: 'bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700/40',
    icon: Clock 
  },
  flexible: { 
    color: 'text-emerald-700 dark:text-emerald-400', 
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700/40',
    icon: CheckCircle2 
  },
};

const JobContractTimelineCard: React.FC<JobContractTimelineCardProps> = ({
  contractStartDate,
  contractEndDate,
  projectDuration,
  urgencyLevel,
}) => {
  if (!contractStartDate && !projectDuration && !urgencyLevel) return null;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getContractStatus = () => {
    if (!contractStartDate) return null;
    const startDate = parseISO(contractStartDate);
    
    if (isToday(startDate)) return { label: 'Starts Today!', color: 'text-emerald-600' };
    if (isPast(startDate) && contractEndDate) {
      const endDate = parseISO(contractEndDate);
      if (isPast(endDate)) return { label: 'Completed', color: 'text-muted-foreground' };
      return { label: 'In Progress', color: 'text-blue-600' };
    }
    if (isFuture(startDate)) {
      const daysUntil = differenceInDays(startDate, new Date());
      if (daysUntil <= 7) return { label: `Starts in ${daysUntil} days`, color: 'text-amber-600' };
      return { label: 'Upcoming', color: 'text-primary' };
    }
    return null;
  };

  const getDurationDays = () => {
    if (!contractStartDate || !contractEndDate) return null;
    try {
      return differenceInDays(parseISO(contractEndDate), parseISO(contractStartDate));
    } catch {
      return null;
    }
  };

  const status = getContractStatus();
  const durationDays = getDurationDays();
  const urgencyConfig = urgencyLevel ? URGENCY_CONFIG[urgencyLevel] : null;
  const UrgencyIcon = urgencyConfig?.icon || Clock;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 overflow-hidden">
      <CardContent className="p-4">
        {/* Urgency Banner */}
        {urgencyLevel && urgencyConfig && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "flex items-center gap-2 p-2.5 rounded-lg border mb-4",
              urgencyConfig.bgColor
            )}
          >
            <UrgencyIcon className={cn("w-5 h-5", urgencyConfig.color)} />
            <div className="flex-1">
              <p className={cn("font-semibold text-sm", urgencyConfig.color)}>
                {URGENCY_LEVEL_LABELS[urgencyLevel]}
              </p>
              <p className="text-xs text-muted-foreground">Start Date Preference</p>
            </div>
          </motion.div>
        )}

        {/* Timeline Visual */}
        {(contractStartDate || contractEndDate) && (
          <div className="relative mb-4">
            {/* Timeline Bar */}
            <div className="absolute top-4 left-6 right-6 h-1 bg-muted rounded-full">
              <motion.div 
                initial={{ width: 0 }}
                animate={{ width: status?.label === 'In Progress' ? '50%' : status?.label === 'Completed' ? '100%' : '0%' }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-primary to-primary/60 rounded-full"
              />
            </div>

            {/* Timeline Points */}
            <div className="flex justify-between items-start pt-1.5">
              {/* Start Date */}
              <div className="flex flex-col items-center">
                <div className={cn(
                  "w-6 h-6 rounded-full border-2 flex items-center justify-center z-10",
                  isPast(parseISO(contractStartDate || '')) 
                    ? "bg-primary border-primary text-primary-foreground" 
                    : "bg-background border-primary"
                )}>
                  <Calendar className="w-3 h-3" />
                </div>
                <div className="text-center mt-2">
                  <p className="text-xs font-medium">Start</p>
                  <p className="text-sm font-semibold">{formatDate(contractStartDate)}</p>
                </div>
              </div>

              {/* Duration in Center */}
              {(projectDuration || durationDays) && (
                <div className="flex flex-col items-center -mt-1">
                  <ArrowRight className="w-5 h-5 text-muted-foreground mb-1" />
                  <Badge variant="secondary" className="text-xs">
                    <Timer className="w-3 h-3 mr-1" />
                    {projectDuration 
                      ? PROJECT_DURATION_LABELS[projectDuration] 
                      : `${durationDays} days`
                    }
                  </Badge>
                </div>
              )}

              {/* End Date */}
              {contractEndDate && (
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "w-6 h-6 rounded-full border-2 flex items-center justify-center z-10",
                    isPast(parseISO(contractEndDate)) 
                      ? "bg-primary border-primary text-primary-foreground" 
                      : "bg-background border-muted-foreground"
                  )}>
                    <CalendarClock className="w-3 h-3" />
                  </div>
                  <div className="text-center mt-2">
                    <p className="text-xs font-medium">End</p>
                    <p className="text-sm font-semibold">{formatDate(contractEndDate)}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Status Badge */}
        {status && (
          <div className="flex justify-center">
            <Badge className={cn("gap-1.5", status.color)}>
              <Clock className="w-3 h-3" />
              {status.label}
            </Badge>
          </div>
        )}

        {/* Duration Only (no dates) */}
        {!contractStartDate && !contractEndDate && projectDuration && (
          <div className="flex items-center justify-center gap-2 p-3 bg-background/50 rounded-lg">
            <Timer className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Project Duration</p>
              <p className="font-semibold">{PROJECT_DURATION_LABELS[projectDuration]}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default JobContractTimelineCard;
